import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Video,
  User,
  AlertCircle,
  Star,
  MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import "react-day-picker/dist/style.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ScheduleSimulation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [pendingSession, setPendingSession] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackSession, setFeedbackSession] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Get available dates from slots
  const availableDates = slots.map(s => new Date(s.date));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get available slots
      const slotsRes = await axios.get(`${API}/simulations/available-slots`);
      setSlots(slotsRes.data?.slots || []);
      
      // Get user's sessions
      const sessionsRes = await axios.get(`${API}/simulations/my-sessions`);
      setSessions(sessionsRes.data?.sessions || []);
      
      // Find pending session (most recent)
      const pending = sessionsRes.data?.sessions?.find(s => s.status === "pending");
      if (pending) {
        setPendingSession(pending);
      }
      
      // If specific session ID, use that
      if (sessionId) {
        const targetSession = sessionsRes.data?.sessions?.find(s => s.id === sessionId);
        if (targetSession) {
          setPendingSession(targetSession);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar los horarios disponibles");
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      const dateStr = date.toISOString().split('T')[0];
      setSelectedDate(dateStr);
      setSelectedTime(null);
    }
  };

  const isDateAvailable = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return slots.some(s => s.date === dateStr);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackSession || rating === 0) {
      toast.error("Por favor selecciona una calificación");
      return;
    }
    
    setSavingFeedback(true);
    try {
      await axios.put(`${API}/simulations/${feedbackSession.id}`, {
        client_feedback: feedback,
        rating: rating
      });
      toast.success("¡Gracias por tu feedback!");
      setShowFeedbackForm(false);
      setFeedbackSession(null);
      setFeedback("");
      setRating(0);
      fetchData();
    } catch (error) {
      toast.error("Error al enviar feedback");
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Selecciona fecha y hora");
      return;
    }
    
    if (!pendingSession) {
      toast.error("No hay sesión pendiente para agendar");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/simulations/schedule/${pendingSession.id}`, {
        scheduled_date: selectedDate,
        scheduled_time: selectedTime,
        notes: notes || null
      });
      
      toast.success("¡Simulacro agendado exitosamente!", {
        duration: 5000,
        icon: "🎉"
      });
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (error) {
      console.error("Error scheduling:", error);
      toast.error(error.response?.data?.detail || "Error al agendar el simulacro");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // If no pending session, show scheduled sessions
  if (!pendingSession) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto" data-testid="my-simulations-page">
          <div className="mb-6">
            <h1 className="font-heading text-2xl font-bold text-navy-900">Mis Simulacros</h1>
            <p className="text-navy-500">Historial de sesiones de práctica</p>
          </div>
          
          {sessions.length === 0 ? (
            <div className="bg-white rounded-xl border border-navy-100 p-8 text-center">
              <Video className="w-12 h-12 text-navy-300 mx-auto mb-4" />
              <h3 className="font-semibold text-navy-700 mb-2">No tienes simulacros</h3>
              <p className="text-navy-500 mb-4">Compra un simulacro con oficial para practicar tu entrevista</p>
              <Button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn">
                Volver al Dashboard
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="bg-white rounded-xl border border-navy-100 p-5"
                  data-testid={`session-card-${session.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        session.status === "completed" ? "bg-emerald-100" :
                        session.status === "scheduled" ? "bg-blue-100" :
                        session.status === "cancelled" ? "bg-red-100" :
                        "bg-amber-100"
                      }`}>
                        {session.status === "completed" ? (
                          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        ) : (
                          <Video className={`w-6 h-6 ${
                            session.status === "scheduled" ? "text-blue-600" :
                            session.status === "cancelled" ? "text-red-600" :
                            "text-amber-600"
                          }`} />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-navy-900">
                          Simulacro de Entrevista
                        </h4>
                        <p className="text-sm text-navy-500">
                          {session.scheduled_date ? (
                            <>
                              {new Date(session.scheduled_date).toLocaleDateString('es-ES', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                              })} a las {session.scheduled_time}
                            </>
                          ) : (
                            "Pendiente de agendar"
                          )}
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      session.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      session.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                      session.status === "cancelled" ? "bg-red-100 text-red-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>
                      {session.status === "completed" ? "Completado" :
                       session.status === "scheduled" ? "Agendado" :
                       session.status === "cancelled" ? "Cancelado" :
                       "Pendiente"}
                    </div>
                  </div>
                  
                  {/* Zoom link for scheduled sessions */}
                  {session.zoom_link && session.status === "scheduled" && (
                    <div className="mt-4 pt-4 border-t border-navy-100">
                      <a href={session.zoom_link} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-blue-600 hover:bg-blue-700" data-testid={`join-zoom-${session.id}`}>
                          <Video className="w-4 h-4 mr-2" />
                          Unirse a Zoom
                        </Button>
                      </a>
                    </div>
                  )}
                  
                  {/* Feedback for completed sessions */}
                  {session.status === "completed" && !session.client_feedback && (
                    <div className="mt-4 pt-4 border-t border-navy-100">
                      <Button 
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => {
                          setFeedbackSession(session);
                          setShowFeedbackForm(true);
                        }}
                        data-testid={`leave-feedback-${session.id}`}
                      >
                        <Star className="w-4 h-4 mr-2" />
                        Dejar Feedback
                      </Button>
                    </div>
                  )}
                  
                  {/* Show existing feedback */}
                  {session.status === "completed" && session.client_feedback && (
                    <div className="mt-4 pt-4 border-t border-navy-100">
                      <div className="bg-emerald-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star 
                                key={star} 
                                className={`w-4 h-4 ${star <= session.rating ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} 
                              />
                            ))}
                          </div>
                          <span className="text-sm text-emerald-700 font-medium">Tu calificación</span>
                        </div>
                        <p className="text-sm text-emerald-600">{session.client_feedback}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Feedback Modal */}
          {showFeedbackForm && feedbackSession && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
              >
                <h3 className="font-semibold text-xl text-navy-900 mb-4">
                  ¿Cómo fue tu experiencia?
                </h3>
                
                {/* Star Rating */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`star-${star}`}
                    >
                      <Star 
                        className={`w-8 h-8 ${star <= rating ? "text-amber-400 fill-amber-400" : "text-gray-300"}`} 
                      />
                    </button>
                  ))}
                </div>
                
                <Textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Cuéntanos sobre tu experiencia..."
                  rows={4}
                  className="mb-4"
                  data-testid="feedback-textarea"
                />
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShowFeedbackForm(false);
                      setFeedbackSession(null);
                      setFeedback("");
                      setRating(0);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleSubmitFeedback}
                    disabled={savingFeedback || rating === 0}
                    data-testid="submit-feedback-btn"
                  >
                    {savingFeedback ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Enviar
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="schedule-simulation-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
              <Video className="w-7 h-7 text-purple-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-navy-900">
                Agendar Simulacro
              </h1>
              <p className="text-navy-500">Selecciona fecha y hora para tu práctica de entrevista</p>
            </div>
          </div>
        </motion.div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <User className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-purple-900">Simulacro con Oficial</h4>
              <p className="text-sm text-purple-700">
                Sesión de 30 minutos vía Zoom con un oficial experto. Recibirás feedback personalizado y tips para tu entrevista real.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Calendar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-navy-100 p-6"
          >
            <h3 className="font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-emerald-600" />
              Selecciona una fecha
            </h3>
            
            {slots.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <p className="text-navy-600">No hay horarios disponibles en este momento</p>
                <p className="text-sm text-navy-500">Por favor intenta más tarde</p>
              </div>
            ) : (
              <div className="flex justify-center">
                <DayPicker
                  mode="single"
                  selected={selectedDate ? new Date(selectedDate) : undefined}
                  onSelect={handleDateSelect}
                  locale={es}
                  disabled={(date) => !isDateAvailable(date)}
                  modifiers={{
                    available: availableDates
                  }}
                  modifiersStyles={{
                    available: {
                      fontWeight: 'bold',
                      color: '#059669'
                    }
                  }}
                  fromDate={new Date()}
                  styles={{
                    caption: { color: '#1e3a5f' },
                    head_cell: { color: '#64748b' },
                    day: { margin: '2px' },
                    day_selected: { 
                      backgroundColor: '#059669',
                      color: 'white',
                      fontWeight: 'bold'
                    },
                    day_today: {
                      color: '#059669',
                      fontWeight: 'bold'
                    }
                  }}
                  className="!font-sans"
                />
              </div>
            )}
            
            {selectedDate && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <p className="text-sm font-medium text-emerald-800">
                  Fecha seleccionada: {new Date(selectedDate).toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            )}
          </motion.div>

          {/* Time Slots */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl border border-navy-100 p-6"
          >
            <h3 className="font-semibold text-navy-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-600" />
              Selecciona una hora
            </h3>
            
            {!selectedDate ? (
              <div className="text-center py-8 text-navy-400">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>Primero selecciona una fecha</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                {slots.find(s => s.date === selectedDate)?.available_times.map((time) => (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedTime === time
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold"
                        : "border-navy-100 hover:border-navy-200 hover:bg-navy-50"
                    }`}
                    data-testid={`time-slot-${time}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            )}

            {/* Notes */}
            {selectedDate && selectedTime && (
              <div className="mt-6">
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  <MessageSquare className="w-4 h-4 inline mr-1" />
                  Notas adicionales (opcional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ej: Tengo preguntas específicas sobre mi caso..."
                  rows={3}
                  data-testid="notes-textarea"
                />
              </div>
            )}
          </motion.div>
        </div>

        {/* Summary & Confirm */}
        {selectedDate && selectedTime && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg mb-1">Confirmar Cita</h3>
                <p className="text-emerald-100">
                  {slots.find(s => s.date === selectedDate)?.day_name},{" "}
                  {new Date(selectedDate).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long'
                  })} a las {selectedTime}
                </p>
              </div>
              <Button
                onClick={handleSchedule}
                disabled={submitting}
                className="bg-white text-emerald-700 hover:bg-emerald-50"
                data-testid="confirm-schedule-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Agendando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Confirmar Cita
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ScheduleSimulation;
